import { apiService } from "@/services/apiService";
import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { DocusealBuilder } from "@docuseal/react";
import { Button } from "@repo/ui/components/Base/Button";
import type {
  DocusealTemplate,
  Submission,
  SubmissionTemplate,
  SubmissionStatus,
  SubmitterTemplate,
} from "@repo/types";
import { PageHeader } from "@/components/page";
import { SubmittersList } from "./SubmittersList";
import { DocViewer } from "@/components/common/DocViewer";
import { ConfirmDialog } from "@repo/ui/components/Base/ConfirmDialog";
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@repo/ui/components/Base/Dialog";
import { Calendar, SendHorizonalIcon } from "lucide-react";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@repo/ui/components/Base/Popover";
import { useUserStore } from "@/stores/userStore";
import { DocumentSignScheduledNotifier } from "./DocumentSignScheduledNotifier";
import { DocumentTemplateSubmittersPanel } from "./DocumentTemplateSubmittersPanel";
import { useFutureCallback } from "@repo/ui/components/Base/hooks/useFutureCallback";
import { useGetDocument } from "@/hooks/queryHooks/documentHooks";
import { useGetTenantByFriendly } from "@/hooks/queryHooks/tenantHooks";
import { useGetUser } from "@/hooks/queryHooks/userHooks";
import { documentRepo } from "@/repositories/documentRepo";

const statusLabels: Record<SubmissionStatus, string> = {
  draft: "Draft",
  sent: "Pending",
  declined: "Declined",
  completed: "Completed",
  archived: "Archived",
};
const DOCUSEAL_TEMPLATE_HAS_NO_FIELDS_MESSAGE =
  "The DocuSeal template has no fields. Add at least one field before sending sign requests.";

const mergeTemplateSubmitters = (
  current: SubmitterTemplate[],
  incoming: DocusealTemplate["submitters"] = []
) => {
  const currentByUuid = new Map(
    current.map((submitter) => [submitter.uuid, submitter])
  );
  const next = incoming.map((incomingSubmitter) => {
    const existing = currentByUuid.get(incomingSubmitter.uuid);

    return {
      uuid: incomingSubmitter.uuid,
      name: incomingSubmitter.name,
      email: existing?.email,
    };
  });

  const changed =
    next.length !== current.length ||
    next.some((submitter, index) => {
      const existing = current[index];

      return (
        !existing ||
        existing.uuid !== submitter.uuid ||
        existing.name !== submitter.name ||
        existing.email !== submitter.email
      );
    });

  return { next, changed };
};

const areTemplateSubmittersEqual = (
  left: SubmitterTemplate[],
  right: SubmitterTemplate[]
) =>
  left.length === right.length &&
  left.every((submitter, index) => {
    const other = right[index];

    return (
      !!other &&
      submitter.uuid === other.uuid &&
      submitter.name === other.name &&
      submitter.email === other.email
    );
  });

const getSendRequestErrorMessage = (error: unknown) => {
  const responseError = error as Record<string, Record<string, string>>;
  const details =
    responseError["error"]?.["details"] ?? responseError["error"]?.["message"];

  if (details === DOCUSEAL_TEMPLATE_HAS_NO_FIELDS_MESSAGE) {
    return "This signing template does not contain any fields yet. Add at least one field in the form before sending sign requests.";
  }

  return details ?? "Failed to send sign request.";
};

const hasTemplateCaughtUp = (
  current: SubmissionTemplate | null | undefined,
  optimistic: SubmissionTemplate | null
) => {
  if (!current || !optimistic) {
    return false;
  }

  return (
    current.status === optimistic.status &&
    (current.submissions?.length ?? 0) >= (optimistic.submissions?.length ?? 0)
  );
};

export const DocumentSignPage = () => {
  const { tenantId, documentId } = useParams();
  const navigate = useNavigate();
  const { data: tenantData } = useGetTenantByFriendly(tenantId ?? "");
  const { data: documentData } = useGetDocument(
    tenantData?.id ?? "",
    documentId ?? ""
  );
  const { data: user, loaded: isUserLoaded } = useGetUser(
    documentData?.submissionTemplate?.userId ?? ""
  );
  const { user: currentUser, activeTeam } = useUserStore((s) => ({
    user: s.user,
    activeTeam: s.activeTeam,
  }));
  const signatureOrchestrator = isUserLoaded && !user ? currentUser : user;

  const [signEditToken, setSignEditToken] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [confirmFinishOpen, setConfirmFinishOpen] = useState(false);
  const [confirmResetTemplateOpen, setConfirmResetTemplateOpen] =
    useState(false);
  const [confirmSendOpen, setConfirmSendOpen] = useState(false);
  const [confirmCancelOpen, setConfirmCancelOpen] = useState(false);
  const [confirmNeedsReviewOpen, setConfirmNeedsReviewOpen] = useState(false);
  const [sendErrorDialogOpen, setSendErrorDialogOpen] = useState(false);

  const [schedulePopoverOpen, setSchedulePopoverOpen] = useState(false);
  const [sending, setSending] = useState(false);
  const [templateSubmitters, setTemplateSubmitters] = useState<
    SubmitterTemplate[]
  >([]);
  const [optimisticSubmissionTemplate, setOptimisticSubmissionTemplate] =
    useState<SubmissionTemplate | null>(null);
  const persistedTemplateSubmitters = useRef<SubmitterTemplate[] | null>(null);
  const [missingEmailSubmitterUuids, setMissingEmailSubmitterUuids] = useState<
    string[]
  >([]);

  const [signedDocumentUrl, setSignedDocumentUrl] = useState<string | null>(
    null
  );
  const submissionTemplate =
    optimisticSubmissionTemplate ?? documentData?.submissionTemplate ?? null;

  useEffect(() => {
    if (persistedTemplateSubmitters.current == null) {
      const submitters = submissionTemplate?.submitters ?? [];
      setTemplateSubmitters(submitters);
      persistedTemplateSubmitters.current = submitters;
    }
  }, [submissionTemplate?.submitters]);

  useEffect(() => {
    if (
      optimisticSubmissionTemplate &&
      hasTemplateCaughtUp(
        documentData?.submissionTemplate,
        optimisticSubmissionTemplate
      )
    ) {
      setOptimisticSubmissionTemplate(null);
    }
  }, [documentData?.submissionTemplate, optimisticSubmissionTemplate]);

  useEffect(() => {
    const signedAttachment =
      documentData && documentRepo.getAttachmentByType(documentData, "signed");
    setSignedDocumentUrl(signedAttachment?.url ?? null);
  }, [documentData]);

  // This effect checks for an active sign request template. If none are found, it creates a new
  // request template. Once created or fetched, it obtains an access token for the editor embed.
  const submissionTemplateId = submissionTemplate?.id;
  const documentLoaded = !!documentData;
  useEffect(() => {
    if (!documentId || !tenantData || !documentLoaded) return;
    let cancel = false;

    (async () => {
      setLoading(true);
      setError(null);

      if (!submissionTemplateId) {
        try {
          await apiService.v1.createSignRequestTemplate(
            tenantData.id,
            documentId,
            {}
          );
        } catch (e) {
          const error = e as Record<string, Record<string, string>>;
          if (error["error"]?.["details"]) {
            setError(error["error"]["details"]);
          }

          setLoading(false);
          return;
        }
      }
      if (cancel) return;

      const signEditToken = await apiService.v1.getSignRequestEditToken(
        tenantData.id,
        documentId
      );
      if (cancel) return;

      setSignEditToken(signEditToken.data.editToken);
      setLoading(false);
    })();

    return () => {
      cancel = true;
    };
  }, [documentLoaded, documentId, submissionTemplateId, tenantData]);

  const templateStatus = submissionTemplate?.status;

  const isDraft = templateStatus === "draft";
  const isSent = templateStatus === "sent";

  const isPreview = !isDraft;

  const submissions = submissionTemplate?.submissions ?? [];
  const lastSubmission = submissions[submissions.length - 1] as
    | Submission
    | undefined;

  const handleGoBack = () => {
    navigate(-1);
  };

  const persistTemplateSubmitters = async (submitters: SubmitterTemplate[]) => {
    if (
      sending ||
      !tenantData?.id ||
      !documentData?.id ||
      !submissionTemplate
    ) {
      return;
    }

    persistedTemplateSubmitters.current = submitters;
    setOptimisticSubmissionTemplate((prev) =>
      prev ? { ...prev, submitters } : prev
    );
    await apiService.v1.updateSubmitters(tenantData.id, documentData.id, {
      submitters,
    });
  };

  const syncTemplateSubmitters = useFutureCallback(
    async (data?: DocusealTemplate | null) => {
      if (sending || !data?.submitters) {
        return;
      }

      let nextSubmitters: SubmitterTemplate[] = templateSubmitters;
      let changed = false;

      setTemplateSubmitters((prev) => {
        const merged = mergeTemplateSubmitters(prev, data.submitters);
        nextSubmitters = merged.next;
        changed = merged.changed;
        return merged.next;
      });

      if (changed) {
        await persistTemplateSubmitters(nextSubmitters);
      }
    }
  );

  const submissionStatus = templateStatus;
  const submissionStatusLabel = statusLabels[submissionStatus ?? "draft"];
  const submittersTeamId =
    activeTeam && activeTeam !== "org"
      ? activeTeam.id
      : documentData?.teamId ?? "";
  const canSend =
    !!templateSubmitters.length &&
    templateSubmitters.every((submitter) => !!submitter.email?.trim());
  const missingEmailSubmitterNames = templateSubmitters
    .filter((submitter) => missingEmailSubmitterUuids.includes(submitter.uuid))
    .map((submitter) => submitter.name);

  const handleTemplateSubmitterEmailChange = (uuid: string, email: string) => {
    if (sending) {
      return;
    }

    setTemplateSubmitters((prev) =>
      prev.map((other) => (other.uuid === uuid ? { ...other, email } : other))
    );
  };

  const handleSend = async () => {
    if (!tenantData?.id || !documentData?.id || !canSend) {
      return;
    }

    setError(null);
    setSending(true);

    try {
      const response = await apiService.v1.sendSignRequest(
        tenantData.id,
        documentData.id,
        {
          submitters: templateSubmitters.map((submitter) => ({
            uuid: submitter.uuid,
            email: submitter.email ?? "",
          })),
        }
      );

      if (response.data.status === "needs_review") {
        setTemplateSubmitters(response.data.submitters);
        persistedTemplateSubmitters.current = response.data.submitters;
        setOptimisticSubmissionTemplate((prev) =>
          prev
            ? {
                ...prev,
                submitters: response.data.submitters,
              }
            : prev
        );
        setMissingEmailSubmitterUuids(response.data.missingEmailSubmitterUuids);
        setConfirmNeedsReviewOpen(true);
      } else if (submissionTemplate) {
        const sentResponse = response.data as typeof response.data & {
          submission: Submission;
        };

        setOptimisticSubmissionTemplate({
          ...submissionTemplate,
          submitters: response.data.submitters,
          status: "sent",
          submissions: [
            ...(submissionTemplate.submissions ?? []),
            sentResponse.submission,
          ],
        });
      }
    } catch (e) {
      setError(getSendRequestErrorMessage(e));
      setSendErrorDialogOpen(true);
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col flex-grow gap-2">
      <ConfirmDialog
        open={confirmCancelOpen}
        setOpen={setConfirmCancelOpen}
        title="Confirm Cancel Sign Requests"
        className="max-w-xl"
        message={
          <>
            Are you sure you want to cancel the signature requests? If not all
            signatures have been applied, this will discard the signatures that
            have been applied, and will allow you to start the signing request
            over from scratch.
          </>
        }
        cancelLabel="Go Back"
        cancelVariant="default"
        confirmLabel="Cancel Sign Requests"
        confirmVariant="destructive"
        onSubmit={async () => {
          await apiService.v1.onSignRequestCancel(
            tenantData?.id ?? "",
            documentData?.id ?? ""
          );
        }}
      />
      <ConfirmDialog
        open={confirmResetTemplateOpen}
        setOpen={setConfirmResetTemplateOpen}
        title="Confirm Reset Sign Requests"
        className="max-w-xl"
        message={
          <>
            Are you sure you want to reset the sign requests? This will clear
            all placed signature fields and other fields, cancel any processed
            or pending signatures, and restart the document signature form with
            the latest version of the document's attached PDF.
            <br />
            <strong>This action cannot be undone.</strong>
          </>
        }
        cancelLabel="Go Back"
        cancelVariant="default"
        confirmLabel="Reset Signatures"
        confirmVariant="destructive"
        onSubmit={async () => {
          await apiService.v1.deleteSignRequestTemplate(
            tenantData?.id ?? "",
            documentData?.id ?? ""
          );
        }}
      />
      <ConfirmDialog
        open={confirmSendOpen}
        setOpen={setConfirmSendOpen}
        title="Confirm Send Signature Requests"
        className="max-w-xl"
        message={
          <>
            This will officially send request emails to all configured signees
            for them to sign this document.
          </>
        }
        cancelLabel="Go Back"
        cancelVariant="default"
        confirmLabel={
          <>
            <SendHorizonalIcon size={20} className="mr-2" /> Send Requests
          </>
        }
        confirmVariant="tertiary"
        onSubmit={async () => {
          await handleSend();
        }}
      />
      <Dialog
        open={sendErrorDialogOpen}
        onOpenChange={(open) => {
          setSendErrorDialogOpen(open);
          if (!open) {
            setError(null);
          }
        }}
      >
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Sign Request Not Sent</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <p>{error ?? "Failed to send sign request."}</p>
          </DialogBody>
          <DialogFooter className="pt-0">
            <Button
              variant="secondary"
              onClick={() => {
                setSendErrorDialogOpen(false);
                setError(null);
              }}
            >
              OK
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <ConfirmDialog
        open={confirmNeedsReviewOpen}
        setOpen={setConfirmNeedsReviewOpen}
        title="Sign Request Not Sent"
        className="max-w-xl"
        message={
          <>
            The sign request was not created because the submitter list changed
            and has been updated from DocuSeal.
            <br />
            Review the current submitters and enter missing email addresses
            before trying again.
            {!!missingEmailSubmitterNames.length && (
              <>
                <br />
                Missing emails: {missingEmailSubmitterNames.join(", ")}.
              </>
            )}
          </>
        }
        cancelLabel="Close"
        cancelVariant="default"
        confirmLabel="OK"
        confirmVariant="secondary"
        onConfirm={() => {
          setMissingEmailSubmitterUuids([]);
        }}
      />
      <ConfirmDialog
        open={confirmFinishOpen}
        setOpen={setConfirmFinishOpen}
        title="Confirm Finalize Sign Requests"
        className="max-w-xl"
        message={
          <>
            This will close any pending sign requests and pull the latest
            version of the signed document. Signees will no longer be able to
            submit signatures.
            <br />
            Are you sure you want to finish the sign requests?
            <br />
            <strong>This action cannot be undone.</strong>
          </>
        }
        cancelLabel="Go Back"
        cancelVariant="default"
        confirmLabel="Finish Sign Requests"
        confirmVariant="secondary"
        onSubmit={async () => {
          await apiService.v1.stopSubmission(
            tenantData?.id ?? "",
            documentData?.id ?? ""
          );
        }}
      />

      <PageHeader
        icon={
          <Button
            type="button"
            className="mr-3"
            onClick={() => {
              handleGoBack();
            }}
          >
            &laquo; Back
          </Button>
        }
        title={`${tenantData?.name ?? "..."}: ${documentData?.name ?? "..."} (${submissionStatusLabel})`}
      >
        <div className="flex gap-2">
          <Popover
            open={schedulePopoverOpen}
            onOpenChange={setSchedulePopoverOpen}
          >
            <PopoverTrigger asChild>
              <Button variant="outline">
                <Calendar />
              </Button>
            </PopoverTrigger>
            <PopoverContent>
              <DocumentSignScheduledNotifier
                tenantId={tenantData?.id ?? ""}
                documentId={documentData?.id ?? ""}
                initialExpirationDate={
                  submissionTemplate?.expirationDate ?? null
                }
                user={signatureOrchestrator}
                onSaved={() => setSchedulePopoverOpen(false)}
              />
            </PopoverContent>
          </Popover>
          {isSent && (
            <Button
              variant="outline"
              onClick={() => setConfirmFinishOpen(true)}
            >
              Finish
            </Button>
          )}
          {!isDraft && (
            <Button
              variant="destructive"
              tooltip="Cancel submission and revert to draft"
              onClick={() => setConfirmCancelOpen(true)}
            >
              Cancel Sign Requests
            </Button>
          )}
          {isDraft && (
            <>
              <Button
                variant="destructive"
                tooltip="Clear placed fields and restart signing process"
                onClick={() => setConfirmResetTemplateOpen(true)}
              >
                Reset Signing
              </Button>
              <Button
                disabled={!canSend || sending}
                onClick={() => setConfirmSendOpen(true)}
              >
                Send
              </Button>
            </>
          )}
        </div>
      </PageHeader>

      {submissionTemplate?.originalAttachmentOutdated && (
        <div className="rounded-md border border-yellow-400 bg-yellow-50 px-4 py-3 text-sm text-yellow-900">
          The original attachment was changed after this signing template was
          prepared. If the fields no longer match the document, reset signing
          prep to rebuild the template from the latest attachment.
        </div>
      )}

      {loading && <p>Loading signing interface...</p>}
      {!loading && error && !sendErrorDialogOpen && (
        <div className="rounded-md border border-red-400 bg-red-50 px-4 py-3 text-sm text-red-900">
          {error}
        </div>
      )}

      {signEditToken && (templateStatus === "draft" || !lastSubmission) && (
        <div className="flex gap-4 h-0 grow">
          <div className="relative flex grow [&_docuseal-builder]:!h-full [&_docuseal-builder]:w-full">
            {sending && (
              <div className="absolute inset-0 z-10 cursor-wait rounded-md bg-white/50" />
            )}
            <DocusealBuilder
              token={signEditToken}
              preview={isPreview}
              previewMode={isPreview}
              withSendButton={false}
              withSignYourselfButton={false}
              withAddPageButton={false}
              withUploadButton={false}
              withDocumentsList={false}
              withTitle={false}
              onSave={syncTemplateSubmitters}
              onLoad={syncTemplateSubmitters}
            />
          </div>
          <DocumentTemplateSubmittersPanel
            tenantId={tenantData?.id ?? ""}
            teamId={submittersTeamId}
            submitters={templateSubmitters}
            disabled={sending}
            onEmailChange={handleTemplateSubmitterEmailChange}
            onEmailBlur={async () => {
              if (sending) {
                return;
              }

              if (
                !areTemplateSubmittersEqual(
                  templateSubmitters,
                  persistedTemplateSubmitters.current ?? []
                )
              ) {
                await persistTemplateSubmitters(templateSubmitters);
              }
            }}
          />
        </div>
      )}

      {!loading && templateStatus !== "draft" && !!lastSubmission && (
        <SubmittersList
          submitters={lastSubmission.submitters}
          tenantId={tenantData?.id ?? ""}
          documentId={documentData?.id ?? ""}
        />
      )}

      {signedDocumentUrl &&
        (templateStatus === "completed" || templateStatus === "archived") && (
          <div className="mt-4">
            <DocViewer documentUri={signedDocumentUrl} />
          </div>
        )}
    </div>
  );
};
